import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  editSubscriptionPlan,
  ERROR_MESSAGES,
  type EditSubscriptionPlanServiceInput,
} from '@/epics/subscriptionManagment/api/editSubscriptionPlanApiService';
import {
  viewSubscriptionPlan,
  type SubscriptionPlan,
} from '@/epics/subscriptionManagment/api/viewSubscriptionPlanApiService';
import { updateSubscriptionStatusApiService } from '@/epics/subscriptionManagment/api/updateSubscriptionStatusApiService';
import { viewOffer } from '@/epics/offerManagment/api/viewOfferApiService';
import { listEquipmentCategories } from '@/epics/facilityManagement/api/listEquipmentCategoriesApiService';
import type { OfferListItem } from '@/epics/offerManagment/types/offerListType';
import { SUBSCRIPTION_ERROR_MESSAGES } from '@/epics/subscriptionManagment/errors';
import {
  getCycleOptions,
  formatPriceWithSpaces,
  getBillingDescription,
  isServiceEntryComplete,
  isServiceEntryEmpty,
  cycleToEnglish,
  EQUIPMENT_RANK_COUNT,
  stripLeadingZeros,
  isPriceTooBig,
  type CreateSubscriptionFormData,
  type FormErrors,
  type OfferDiscountInfo,
} from './useCreateSubscription';
import {
  BILLING_CYCLE_LABELS,
  formatDate,
  formatDuration,
  getUserDisplayName,
} from './useViewSubscriptionPlanContent';
import type {
  ServiceConfigEntry,
  ServiceConfigFieldErrors,
} from '@/epics/subscriptionManagment/components/ServiceConfigCard';
import {
  computeFinalPrice,
  computeOfferFinalPrice,
  getCycleSuffix,
} from '@/epics/subscriptionManagment/utils/pricing';
import type { PlanPreviewData } from '@/epics/subscriptionManagment/components/PlanPreviewAccordion';

// ============================================================================
// Types
// ============================================================================

export type PlanDetailsStepProps = {
  formData: CreateSubscriptionFormData;
  setFormData: React.Dispatch<React.SetStateAction<CreateSubscriptionFormData>>;
  durationType: string;
  setDurationType: (id: string) => void;
  period: string;
  setPeriod: (id: string) => void;
  cycle: string;
  setCycle: (id: string) => void;
  cycleOptions: { id: string; label: string }[];
  errors: FormErrors;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  formatPriceWithSpaces: (price: string) => string;
  getBillingDescription: (
    price: string,
    cycle: string,
    period: string,
    formatPriceWithSpacesFn: (p: string) => string
  ) => string;
  isSubmitting: boolean;
  isPreviewStep: boolean;
  modifiedBy: string;
  modifiedDate: string;
  selectedOffer: string;
  offerName: string;
  offerDiscount: OfferDiscountInfo | null;
  handleOfferChange: (id: string, offer?: OfferListItem) => void;
};

export type ServicesStepProps = {
  isSubmitting: boolean;
  isPreviewStep: boolean;
  services: ServiceConfigEntry[];
  expandedServiceKey: string | null;
  serviceFieldErrors: Record<string, ServiceConfigFieldErrors>;
  servicesError: string;
  hasIncompleteService: boolean;
  handleAddService: () => void;
  handleServiceTypeChange: (
    key: string,
    serviceTypeId: string,
    serviceName: string
  ) => void;
  handleServiceFieldChange: (
    key: string,
    field: 'usageLimit' | 'basePrice' | 'discountPercentage',
    value: string
  ) => void;
  handleToggleExpandService: (key: string) => void;
  handleRequestRemoveService: (key: string) => void;
};

export interface EquipmentCategoryOption {
  id: string;
  label: string;
}

export type EquipmentPricingStepProps = {
  isSubmitting: boolean;
  isPreviewStep: boolean;
  equipmentCategoryOptions: EquipmentCategoryOption[];
  equipmentCategoriesLoading: boolean;
  equipmentPrices: Record<string, string>;
  equipmentPriceErrors: Record<string, string>;
  handleEquipmentPriceChange: (categoryId: string, value: string) => void;
};

export type DiscountLogicStepProps = {
  isSubmitting: boolean;
  isPreviewStep: boolean;
  discountTiers: Record<number, string>;
  discountTierErrors: Record<number, string>;
  handleDiscountTierChange: (rank: number, value: string) => void;
};

// ============================================================================
// Mappers: API data → Form values
// ============================================================================

/**
 * Maps API durationType (MONTHLY/YEARLY) to form value (Miesięczny/Roczny)
 */
const mapDurationTypeFromApi = (
  durationType: SubscriptionPlan['durationType']
): string => {
  return durationType === 'YEARLY' ? 'Roczny' : 'Miesięczny';
};

/**
 * Maps API billingCycle (MONTHLY/QUARTERLY/YEARLY) to form value (Miesięczny/kwartalny/Roczny)
 */
const mapBillingCycleFromApi = (
  billingCycle: SubscriptionPlan['billingCycle']
): string => {
  const label = BILLING_CYCLE_LABELS[billingCycle];
  return label || 'Miesięczny';
};

/**
 * Maps API duration + durationType to form period value
 */
const mapPeriodFromApi = (
  duration: number,
  durationType: SubscriptionPlan['durationType']
): string => {
  return formatDuration(duration, durationType);
};

/**
 * Maps API price to form price string (with spaces for formatting)
 */
const mapPriceFromApi = (price: number): string => {
  return String(price);
};

/**
 * Maps SubscriptionPlan to form data
 */
const mapPlanToFormData = (
  plan: SubscriptionPlan
): CreateSubscriptionFormData => {
  return {
    planName: plan.planName || '',
    description: plan.planDescription || '',
    price: mapPriceFromApi(plan.price),
    userCount: plan.limits?.numberOfUsers?.toString() || '',
    facilityCount: plan.limits?.numberOfFacilities?.toString() || '',
    qmsDocCount: plan.limits?.numberOfQmsDocuments?.toString() || '',
  };
};

/**
 * Maps the plan's existing services (API entries) to ServiceConfigEntry form
 * rows, carrying each row's real DB id through — this is what lets a save
 * later tell the backend "update this row in place" instead of treating an
 * unchanged existing service as a brand-new one.
 */
const mapServicesFromApi = (
  services: SubscriptionPlan['services']
): ServiceConfigEntry[] => {
  return (services ?? []).map((service) => ({
    key: service.id,
    id: service.id,
    serviceTypeId: service.serviceTypeId,
    serviceName: service.serviceName || '',
    usageLimit:
      service.usageLimit !== null && service.usageLimit !== undefined
        ? String(service.usageLimit)
        : '',
    basePrice:
      service.basePrice !== null && service.basePrice !== undefined
        ? String(service.basePrice)
        : '',
    discountPercentage:
      service.discountPercentage !== null &&
      service.discountPercentage !== undefined
        ? String(service.discountPercentage)
        : '',
  }));
};

/**
 * Maps the plan's existing equipment configs (API entries) to a base-price-
 * by-category-id record. This is NOT the option list to render — the plan
 * may only have rows for categories that existed at creation time. The
 * option list itself must be the full CURRENT active equipment_categories
 * set (fetched separately, same as Create), so a category activated after
 * this plan was created still shows up here and must be priced before
 * saving — matching the backend's exact-match validation.
 */
const mapEquipmentPriceMapFromApi = (
  equipmentCategories: SubscriptionPlan['equipmentCategories']
): Record<string, string> => {
  const prices: Record<string, string> = {};
  (equipmentCategories ?? []).forEach((entry) => {
    prices[entry.equipmentCategoryId] = String(entry.basePrice);
  });
  return prices;
};

/** Maps the plan's discount ladder (API entries) to a rank → percentage record. */
const mapDiscountTiersFromApi = (
  discountTiers: SubscriptionPlan['discountTiers']
): Record<number, string> => {
  const mapped: Record<number, string> = {};
  (discountTiers ?? []).forEach((tier) => {
    mapped[tier.equipmentRank] = String(tier.discountPercentage);
  });
  return mapped;
};

/** Order-independent comparison of a string-keyed record (base prices / discount %s). */
const normalizeRecordForCompare = (
  record: Record<string, string> | Record<number, string>
): string =>
  JSON.stringify(Object.entries(record).sort(([a], [b]) => a.localeCompare(b)));

/** Order-independent comparison of the relevant fields of two service lists. */
const normalizeServicesForCompare = (services: ServiceConfigEntry[]) =>
  [...services]
    .map((s) => ({
      id: s.id ?? null,
      serviceTypeId: s.serviceTypeId,
      usageLimit: s.usageLimit,
      basePrice: s.basePrice,
      discountPercentage: s.discountPercentage,
    }))
    .sort(
      (a, b) =>
        (a.id ?? '').localeCompare(b.id ?? '') ||
        a.serviceTypeId.localeCompare(b.serviceTypeId)
    );

// ============================================================================
// Hook
// ============================================================================

export function useEditSubscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId } = useParams<{ planId: string }>();

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);

  // Track if data has been loaded to prevent reloading on navigation (use ref to avoid re-renders)
  const hasLoadedDataRef = useRef(false);

  // Sync isPreviewStep with URL path
  const isPreviewInUrl = useMemo(
    () => location.pathname.includes('/Podgląd-planu'),
    [location.pathname]
  );

  // Form state
  const [durationType, setDurationType] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [cycle, setCycle] = useState<string>('');
  const [formData, setFormData] = useState<CreateSubscriptionFormData>({
    planName: '',
    description: '',
    price: '',
    userCount: '',
    facilityCount: '',
    qmsDocCount: '',
  });
  const [originalFormData, setOriginalFormData] =
    useState<CreateSubscriptionFormData>({
      planName: '',
      description: '',
      price: '',
      userCount: '',
      facilityCount: '',
      qmsDocCount: '',
    });
  const [originalDurationType, setOriginalDurationType] = useState<string>('');
  const [originalPeriod, setOriginalPeriod] = useState<string>('');
  const [originalCycle, setOriginalCycle] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Read-only fields
  const [modifiedBy, setModifiedBy] = useState<string>('—');
  const [modifiedDate, setModifiedDate] = useState<string>('—');

  // Status state
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [activeClientCount, setActiveClientCount] = useState<number>(0);

  // Wizard step — full 4-tab structure, mirroring Create: Plan Details /
  // Services / Equipment Pricing / Discount Logic. Initialized from the URL,
  // not always 1, so refreshing the browser while sitting on the preview URL
  // doesn't desync `step` from a URL that implies every step was completed.
  const [step, setStep] = useState<1 | 2 | 3 | 4>(isPreviewInUrl ? 4 : 1);

  // UI state - sync with URL
  const [isPreviewStep, setIsPreviewStep] = useState(isPreviewInUrl);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<string>('');
  const [originalSelectedOffer, setOriginalSelectedOffer] =
    useState<string>('');
  const [offerDiscount, setOfferDiscount] = useState<OfferDiscountInfo | null>(
    null
  );
  // Only used to display the offer's name on the preview screen — the
  // dropdown itself resolves selectedOffer to a display value independently.
  const [offerName, setOfferName] = useState<string>('');

  // Services state
  const [services, setServices] = useState<ServiceConfigEntry[]>([]);
  const [originalServices, setOriginalServices] = useState<
    ServiceConfigEntry[]
  >([]);
  const [expandedServiceKey, setExpandedServiceKey] = useState<string | null>(
    null
  );
  const [serviceFieldErrors, setServiceFieldErrors] = useState<
    Record<string, ServiceConfigFieldErrors>
  >({});
  const [servicesError, setServicesError] = useState('');
  const [removeDialogKey, setRemoveDialogKey] = useState<string | null>(null);

  // Equipment Pricing state. The option list is the full CURRENT active
  // equipment_categories set (fetched independently, same as Create) — NOT
  // just whatever categories the plan already has rows for, since a category
  // activated after this plan was created wouldn't be in the plan's existing
  // configs yet, but the backend still requires exact-match pricing for it.
  const [equipmentCategoryOptions, setEquipmentCategoryOptions] = useState<
    EquipmentCategoryOption[]
  >([]);
  const [equipmentCategoriesLoading, setEquipmentCategoriesLoading] =
    useState(false);
  // Raw base-price-by-category-id from the plan's existing configs — merged
  // onto `equipmentCategoryOptions` once both are loaded (see merge effect
  // below); a category with no existing row here just defaults to ''.
  const [planEquipmentPriceMap, setPlanEquipmentPriceMap] = useState<Record<
    string,
    string
  > | null>(null);
  const hasMergedEquipmentPricesRef = useRef(false);
  const [equipmentPrices, setEquipmentPrices] = useState<
    Record<string, string>
  >({});
  const [originalEquipmentPrices, setOriginalEquipmentPrices] = useState<
    Record<string, string>
  >({});
  const [equipmentPriceErrors, setEquipmentPriceErrors] = useState<
    Record<string, string>
  >({});

  // Discount Logic state
  const [discountTiers, setDiscountTiers] = useState<Record<number, string>>(
    {}
  );
  const [originalDiscountTiers, setOriginalDiscountTiers] = useState<
    Record<number, string>
  >({});
  const [discountTierErrors, setDiscountTierErrors] = useState<
    Record<number, string>
  >({});

  // Status dialog state
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState<boolean>(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState<boolean>(false);

  // Status update snackbar state
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState<boolean>(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState<string>('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] =
    useState<boolean>(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState<string>('');

  // Derived
  const cycleOptions = getCycleOptions(durationType, period);
  const removeDialogEntry =
    services.find((s) => s.key === removeDialogKey) ?? null;
  // Block adding another service while any existing one is still missing a
  // required field — otherwise incomplete cards could pile up unbounded.
  const hasIncompleteService = services.some(
    (entry) => !isServiceEntryComplete(entry)
  );

  const servicesChanged = useMemo(
    () =>
      JSON.stringify(normalizeServicesForCompare(services)) !==
      JSON.stringify(normalizeServicesForCompare(originalServices)),
    [services, originalServices]
  );

  const equipmentPricesChanged = useMemo(
    () =>
      normalizeRecordForCompare(equipmentPrices) !==
      normalizeRecordForCompare(originalEquipmentPrices),
    [equipmentPrices, originalEquipmentPrices]
  );

  const discountTiersChanged = useMemo(
    () =>
      normalizeRecordForCompare(discountTiers) !==
      normalizeRecordForCompare(originalDiscountTiers),
    [discountTiers, originalDiscountTiers]
  );

  // Plain, pre-formatted data for the read-only preview screen — kept
  // decoupled from PlanPreviewAccordion's own internals.
  const previewData: PlanPreviewData = useMemo(() => {
    const priceNum =
      parseFloat(
        (formData.price || '0').replace(/\s/g, '').replace(',', '.')
      ) || 0;
    const finalPriceNum = computeOfferFinalPrice(priceNum, offerDiscount);
    const cycleSuffix = getCycleSuffix(cycleToEnglish(cycle));

    return {
      planDetails: {
        planName: formData.planName,
        durationType,
        period,
        billingCycle: cycleOptions.find((o) => o.id === cycle)?.label ?? cycle,
        description: formData.description,
        price: `${formatPriceWithSpaces(formData.price)} zł`,
        userCount: formData.userCount,
        facilityCount: formData.facilityCount,
        qmsDocCount: formData.qmsDocCount,
        offer:
          selectedOffer && offerName
            ? {
                name: offerName,
                discountLabel:
                  offerDiscount?.discountType === 'PERCENTAGE'
                    ? `${offerDiscount.discountValue}% off`
                    : offerDiscount
                      ? `${formatPriceWithSpaces(offerDiscount.discountValue.toFixed(2))} zł off`
                      : '',
              }
            : null,
        finalPrice: `${formatPriceWithSpaces(finalPriceNum.toFixed(2))} zł${cycleSuffix}`,
      },
      services: services.map((entry) => {
        const basePriceNum = parseFloat(entry.basePrice) || 0;
        const discountNum = entry.discountPercentage
          ? parseFloat(entry.discountPercentage)
          : null;
        return {
          key: entry.key,
          serviceName: entry.serviceName || '—',
          usageLimit: entry.usageLimit || '—',
          basePrice: `${formatPriceWithSpaces(entry.basePrice || '0')} zł`,
          discountLabel: discountNum ? `${discountNum}%` : '0%',
          finalPrice: `${formatPriceWithSpaces(computeFinalPrice(basePriceNum, discountNum).toFixed(2))} zł`,
        };
      }),
      equipmentCategories: equipmentCategoryOptions.map((category) => ({
        id: category.id,
        categoryLabel: category.label,
        basePrice: `${formatPriceWithSpaces(equipmentPrices[category.id] || '0')} zł`,
      })),
      discountTiers: Array.from({ length: EQUIPMENT_RANK_COUNT }, (_, i) => ({
        rank: i + 1,
        discountLabel: `${discountTiers[i + 1] ?? '0'}%`,
      })),
    };
  }, [
    formData,
    durationType,
    period,
    cycle,
    cycleOptions,
    selectedOffer,
    offerName,
    offerDiscount,
    services,
    equipmentCategoryOptions,
    equipmentPrices,
    discountTiers,
  ]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    if (
      formData.planName !== originalFormData.planName ||
      formData.description !== originalFormData.description ||
      formData.price !== originalFormData.price ||
      formData.userCount !== originalFormData.userCount ||
      formData.facilityCount !== originalFormData.facilityCount ||
      formData.qmsDocCount !== originalFormData.qmsDocCount ||
      durationType !== originalDurationType ||
      period !== originalPeriod ||
      cycle !== originalCycle ||
      selectedOffer !== originalSelectedOffer ||
      servicesChanged ||
      equipmentPricesChanged ||
      discountTiersChanged
    ) {
      return true;
    }
    return false;
  }, [
    formData,
    originalFormData,
    durationType,
    originalDurationType,
    period,
    originalPeriod,
    cycle,
    originalCycle,
    selectedOffer,
    originalSelectedOffer,
    servicesChanged,
    equipmentPricesChanged,
    discountTiersChanged,
  ]);

  // Sync isPreviewStep with URL changes
  useEffect(() => {
    setIsPreviewStep(isPreviewInUrl);
  }, [isPreviewInUrl]);

  // Reset hasLoadedDataRef when planId changes
  useEffect(() => {
    hasLoadedDataRef.current = false;
    hasMergedEquipmentPricesRef.current = false;
    setPlanEquipmentPriceMap(null);
  }, [planId]);

  // The equipment category list is the full CURRENT active catalog — fetched
  // once, independent of which plan is being edited, same as Create. A
  // category activated after this plan was created must still show up here
  // and be priced before saving (backend enforces the exact-match rule).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setEquipmentCategoriesLoading(true);
      try {
        const result = await listEquipmentCategories({
          page: 1,
          limit: 100,
          status: 'active',
        });
        if (!cancelled) {
          setEquipmentCategoryOptions(result.data);
        }
      } finally {
        if (!cancelled) {
          setEquipmentCategoriesLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Merge the plan's existing per-category prices onto the full active
  // category list once both are available — a category with no existing
  // row on the plan defaults to '' (required, so it must be filled in
  // before saving). Runs once per planId via the ref guard, so it doesn't
  // clobber in-progress edits if either source re-renders afterward.
  useEffect(() => {
    if (
      equipmentCategoryOptions.length === 0 ||
      planEquipmentPriceMap === null ||
      hasMergedEquipmentPricesRef.current
    ) {
      return;
    }
    const merged: Record<string, string> = {};
    equipmentCategoryOptions.forEach((option) => {
      merged[option.id] = planEquipmentPriceMap[option.id] ?? '';
    });
    setEquipmentPrices(merged);
    setOriginalEquipmentPrices(merged);
    hasMergedEquipmentPricesRef.current = true;
  }, [equipmentCategoryOptions, planEquipmentPriceMap]);

  // Load plan data - only once per planId
  useEffect(() => {
    const loadPlanData = async () => {
      if (!planId) {
        setIsLoading(false);
        return;
      }

      // Don't reload if data is already loaded (prevents resetting form on navigation)
      // Also check if form already has data (planName is a good indicator)
      if (hasLoadedDataRef.current || formData.planName.trim() !== '') {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await viewSubscriptionPlan({ planId });
        if (response.forbidden) {
          setAccessError(true);
          setIsLoading(false);
          return;
        }
        if (response.success && response.data) {
          const plan = response.data;

          // Map API data to form values
          const mappedFormData = mapPlanToFormData(plan);
          const mappedDurationType = mapDurationTypeFromApi(plan.durationType);
          const mappedPeriod = mapPeriodFromApi(
            plan.duration,
            plan.durationType
          );
          const mappedCycle = mapBillingCycleFromApi(plan.billingCycle);

          // Set form data
          setFormData(mappedFormData);
          setOriginalFormData(mappedFormData);
          setDurationType(mappedDurationType);
          setOriginalDurationType(mappedDurationType);
          setPeriod(mappedPeriod);
          setOriginalPeriod(mappedPeriod);
          setCycle(mappedCycle);
          setOriginalCycle(mappedCycle);

          // Set read-only fields — a plan that's never been modified since
          // creation has no updatedBy, so fall back to who created it.
          const updatedByName = getUserDisplayName(plan.updatedBy);
          setModifiedBy(
            updatedByName === '—'
              ? getUserDisplayName(plan.createdBy)
              : updatedByName
          );
          setModifiedDate(formatDate(plan.updatedAt));

          // Set status
          setStatus(plan.status || 'active');
          setActiveClientCount(plan.activeClientCount ?? 0);

          // Set selected offer
          const mappedOfferId = plan.offer?.offerId || '';
          setSelectedOffer(mappedOfferId);
          setOriginalSelectedOffer(mappedOfferId);
          setOfferName(plan.offer?.offerName || '');

          // The offer dropdown only reports discountType/discountValue via
          // its onChange when the user actively picks something — on initial
          // load with a pre-assigned offer, nothing fires onChange, so the
          // discount and name for the Applied Offer preview have to be fetched here.
          // Awaited (not fire-and-forget) so isLoading stays true until the
          // offer name is resolved — otherwise the dropdown briefly renders
          // with the raw offerId before this resolves.
          if (mappedOfferId) {
            const offerResult = await viewOffer({ offerId: mappedOfferId });
            if (offerResult.success && offerResult.data) {
              setOfferName(offerResult.data.offerName || '');
              setOfferDiscount({
                discountType: offerResult.data.discountType as
                  | 'PERCENTAGE'
                  | 'FIXED',
                discountValue: offerResult.data.discountValue,
              });
            }
          }

          // Map services
          const mappedServices = mapServicesFromApi(plan.services);
          setServices(mappedServices);
          setOriginalServices(mappedServices);

          // Store the plan's existing per-category prices raw — merged onto
          // the full active category list by the merge effect above once
          // both are loaded (fetch order between the two isn't guaranteed).
          setPlanEquipmentPriceMap(
            mapEquipmentPriceMapFromApi(plan.equipmentCategories)
          );

          const mappedTiers = mapDiscountTiersFromApi(plan.discountTiers);
          setDiscountTiers(mappedTiers);
          setOriginalDiscountTiers(mappedTiers);

          // Mark data as loaded (using ref to avoid re-renders)
          hasLoadedDataRef.current = true;
        } else {
          // Error loading plan - navigate to list
          navigate('/Plany', { replace: true });
        }
      } catch (error) {
        console.error('Error loading plan data:', error);
        navigate('/Plany', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    loadPlanData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  // Reset period and cycle when period type changes
  useEffect(() => {
    if (durationType !== originalDurationType) {
      setPeriod('');
      setCycle('');
    }
  }, [durationType, originalDurationType]);

  // Reset cycle when period changes
  useEffect(() => {
    if (period !== originalPeriod) {
      setCycle('');
    }
  }, [period, originalPeriod]);

  // Auto-fill cycle when there is only one option
  useEffect(() => {
    const options = getCycleOptions(durationType, period);
    const singleOption = options[0];
    if (options.length === 1 && singleOption && !cycle) {
      setErrors((prev) => ({ ...prev, cycle: '' }));
      setCycle(singleOption.id);
    }
  }, [durationType, period, cycle]);

  // --------------------------------------------------------------------------
  // Step 1 (Plan Details) validation — extracted so both the "Next" submit
  // and step-tab navigation can run the same check before advancing.
  // --------------------------------------------------------------------------

  const validateStep1 = useCallback((): FormErrors => {
    const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
    const newErrors: FormErrors = {};

    if (!formData.planName.trim()) {
      newErrors.planName = requiredMessage;
    }
    if (!formData.description.trim()) {
      newErrors.description = requiredMessage;
    }
    if (!durationType) {
      newErrors.durationType = requiredMessage;
    }
    if (!period) {
      newErrors.period = requiredMessage;
    }
    if (!cycle) {
      newErrors.cycle = requiredMessage;
    }
    const priceNum = parseFloat(formData.price.replace(/\s/g, ''));
    if (
      formData.price.trim() === '' ||
      Number.isNaN(priceNum) ||
      priceNum < 0
    ) {
      newErrors.price = requiredMessage;
    } else if (isPriceTooBig(formData.price)) {
      newErrors.price = SUBSCRIPTION_ERROR_MESSAGES.PRICE_TOO_BIG;
    }
    if (!formData.userCount.trim()) {
      newErrors.userCount = requiredMessage;
    }
    if (!formData.facilityCount.trim()) {
      newErrors.facilityCount = requiredMessage;
    }

    return newErrors;
  }, [formData, durationType, period, cycle]);

  // Tick marks are validity-driven here (EDIT flow), unlike ADD's event-driven
  // one: the record is already fully populated, so every step's tick reflects
  // its *current* data on every render — no click history involved. The
  // active step never ticks regardless (StepTabNav suppresses it separately).
  const completedSteps = useMemo(() => {
    const next = new Set<number>();
    if (Object.keys(validateStep1()).length === 0) {
      next.add(1);
    }
    if (services.length > 0 && services.every(isServiceEntryComplete)) {
      next.add(2);
    }
    if (
      equipmentCategoryOptions.length > 0 &&
      equipmentCategoryOptions.every((option) => {
        const raw = equipmentPrices[option.id] ?? '';
        const priceNum = parseFloat(raw.replace(',', '.'));
        return raw.trim() !== '' && !Number.isNaN(priceNum) && priceNum >= 0;
      })
    ) {
      next.add(3);
    }
    if (
      Array.from({ length: EQUIPMENT_RANK_COUNT }, (_, i) => i + 1).every(
        (rank) => {
          const raw = discountTiers[rank] ?? '';
          const pctNum = Number(raw);
          return (
            raw.trim() !== '' &&
            !Number.isNaN(pctNum) &&
            pctNum >= 0 &&
            pctNum <= 100
          );
        }
      )
    ) {
      next.add(4);
    }
    return next;
  }, [
    validateStep1,
    services,
    equipmentCategoryOptions,
    equipmentPrices,
    discountTiers,
  ]);

  // --------------------------------------------------------------------------
  // Offer handlers
  // --------------------------------------------------------------------------

  const handleOfferChange = useCallback((id: string, offer?: OfferListItem) => {
    setSelectedOffer(id);
    setOfferDiscount(
      offer
        ? {
            discountType: offer.discountType,
            discountValue: offer.discountValue,
          }
        : null
    );
    setOfferName(offer?.offerName ?? '');
  }, []);

  // --------------------------------------------------------------------------
  // Services handlers
  // --------------------------------------------------------------------------

  const handleAddService = useCallback(() => {
    const key = crypto.randomUUID();
    setServices((prev) => [
      ...prev,
      {
        key,
        serviceTypeId: '',
        serviceName: '',
        usageLimit: '',
        basePrice: '',
        discountPercentage: '',
      },
    ]);
    setExpandedServiceKey(key);
    setServicesError('');
  }, []);

  // The Services section never sits empty — if the user removes every
  // service down to zero, a blank card reopens automatically (mirrors
  // Create's step-2 behavior). Guarded on isLoading so this doesn't fire
  // and add a spurious blank card before the plan's real services arrive.
  useEffect(() => {
    if (!isLoading && services.length === 0) {
      handleAddService();
    }
  }, [isLoading, services.length, handleAddService]);

  const handleServiceTypeChange = useCallback(
    (key: string, serviceTypeId: string, serviceName: string) => {
      setServices((prev) =>
        prev.map((entry) =>
          entry.key === key ? { ...entry, serviceTypeId, serviceName } : entry
        )
      );
      setServiceFieldErrors((prev) => {
        if (!prev[key]?.serviceTypeId) {
          return prev;
        }
        return { ...prev, [key]: { ...prev[key], serviceTypeId: '' } };
      });
    },
    []
  );

  const handleServiceFieldChange = useCallback(
    (
      key: string,
      field: 'usageLimit' | 'basePrice' | 'discountPercentage',
      value: string
    ) => {
      const nextValue =
        field === 'discountPercentage' ? stripLeadingZeros(value) : value;
      setServices((prev) =>
        prev.map((entry) =>
          entry.key === key ? { ...entry, [field]: nextValue } : entry
        )
      );
      setServiceFieldErrors((prev) => {
        if (!prev[key]) {
          return prev;
        }
        return { ...prev, [key]: { ...prev[key], [field]: '' } };
      });
    },
    []
  );

  const handleToggleExpandService = useCallback((key: string) => {
    setExpandedServiceKey((prev) => (prev === key ? null : key));
  }, []);

  const handleRequestRemoveService = useCallback(
    (key: string) => {
      const entry = services.find((s) => s.key === key);
      if (!entry) {
        return;
      }
      if (isServiceEntryEmpty(entry)) {
        setServices((prev) => prev.filter((s) => s.key !== key));
        setExpandedServiceKey((prev) => (prev === key ? null : prev));
      } else {
        setRemoveDialogKey(key);
      }
    },
    [services]
  );

  const handleCloseRemoveDialog = useCallback(() => {
    setRemoveDialogKey(null);
  }, []);

  const handleConfirmRemoveService = useCallback(() => {
    setServices((prev) => prev.filter((s) => s.key !== removeDialogKey));
    setExpandedServiceKey((prev) => (prev === removeDialogKey ? null : prev));
    setRemoveDialogKey(null);
  }, [removeDialogKey]);

  // --------------------------------------------------------------------------
  // Equipment Pricing (step 3) / Discount Logic (step 4) handlers
  // --------------------------------------------------------------------------

  const handleEquipmentPriceChange = useCallback(
    (categoryId: string, value: string) => {
      setEquipmentPrices((prev) => ({
        ...prev,
        [categoryId]: value,
      }));
      setEquipmentPriceErrors((prev) => {
        if (!prev[categoryId]) {
          return prev;
        }
        const next = { ...prev };
        delete next[categoryId];
        return next;
      });
    },
    []
  );

  const handleDiscountTierChange = useCallback(
    (rank: number, value: string) => {
      setDiscountTiers((prev) => ({ ...prev, [rank]: value }));
      setDiscountTierErrors((prev) => {
        if (!prev[rank]) {
          return prev;
        }
        const next = { ...prev };
        delete next[rank];
        return next;
      });
    },
    []
  );

  const validateServicesStep = useCallback((): {
    fieldErrors: Record<string, ServiceConfigFieldErrors>;
    firstIncompleteKey: string | null;
    generalError: string;
  } => {
    const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
    const nextFieldErrors: Record<string, ServiceConfigFieldErrors> = {};
    let firstIncompleteKey: string | null = null;

    for (const entry of services) {
      const entryErrors: ServiceConfigFieldErrors = {};
      if (!entry.serviceTypeId.trim()) {
        entryErrors.serviceTypeId = requiredMessage;
      }
      // Usage Limit only matters when a discount is applied — see
      // isServiceEntryComplete for the same rule.
      if (
        entry.discountPercentage.trim() !== '' &&
        (!entry.usageLimit.trim() || Number(entry.usageLimit) < 1)
      ) {
        entryErrors.usageLimit = requiredMessage;
      }
      if (!entry.basePrice.trim()) {
        entryErrors.basePrice = requiredMessage;
      } else if (isPriceTooBig(entry.basePrice)) {
        // Not clamped on input (see ServiceConfigCard) — an oversized value
        // must surface as an error here instead of being silently rewritten.
        entryErrors.basePrice = SUBSCRIPTION_ERROR_MESSAGES.INVALID_BASE_PRICE;
      }
      if (Object.keys(entryErrors).length > 0) {
        nextFieldErrors[entry.key] = entryErrors;
        firstIncompleteKey ??= entry.key;
      }
    }

    if (firstIncompleteKey) {
      return {
        fieldErrors: nextFieldErrors,
        firstIncompleteKey,
        generalError: '',
      };
    }
    if (services.length === 0) {
      return {
        fieldErrors: {},
        firstIncompleteKey: null,
        generalError: 'At least one service must be selected',
      };
    }
    return { fieldErrors: {}, firstIncompleteKey: null, generalError: '' };
  }, [services]);

  const validateStep3EquipmentPrices = useCallback((): Record<
    string,
    string
  > => {
    const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
    const newErrors: Record<string, string> = {};
    for (const category of equipmentCategoryOptions) {
      const raw = equipmentPrices[category.id] ?? '';
      const priceNum = parseFloat(raw.replace(',', '.'));
      if (raw.trim() === '' || Number.isNaN(priceNum) || priceNum < 0) {
        newErrors[category.id] = requiredMessage;
      } else if (isPriceTooBig(raw)) {
        newErrors[category.id] = SUBSCRIPTION_ERROR_MESSAGES.INVALID_BASE_PRICE;
      }
    }
    return newErrors;
  }, [equipmentCategoryOptions, equipmentPrices]);

  const validateStep4DiscountTiers = useCallback((): Record<number, string> => {
    const requiredMessage = SUBSCRIPTION_ERROR_MESSAGES.REQUIRED;
    const newErrors: Record<number, string> = {};
    for (let rank = 1; rank <= EQUIPMENT_RANK_COUNT; rank += 1) {
      const raw = discountTiers[rank] ?? '';
      const pctNum = Number(raw);
      if (
        raw.trim() === '' ||
        Number.isNaN(pctNum) ||
        pctNum < 0 ||
        pctNum > 100
      ) {
        newErrors[rank] = requiredMessage;
      }
    }
    return newErrors;
  }, [discountTiers]);

  const handleStepTabClick = useCallback(
    (targetStep: number) => {
      if (targetStep === step) {
        return;
      }

      // Leaving any step (either direction) requires its own fields to be
      // valid first — same rules each step's own "Next" button enforces.
      if (step === 1) {
        const newErrors = validateStep1();
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
          return;
        }
      } else if (step === 2) {
        const { fieldErrors, firstIncompleteKey, generalError } =
          validateServicesStep();
        if (firstIncompleteKey) {
          setServiceFieldErrors(fieldErrors);
          setExpandedServiceKey(firstIncompleteKey);
          setServicesError('');
          return;
        }
        if (generalError) {
          setServicesError(generalError);
          return;
        }
        setServicesError('');
      } else if (step === 3) {
        const priceErrors = validateStep3EquipmentPrices();
        if (Object.keys(priceErrors).length > 0) {
          setEquipmentPriceErrors(priceErrors);
          return;
        }
        setEquipmentPriceErrors({});
      } else if (step === 4) {
        const tierErrors = validateStep4DiscountTiers();
        if (Object.keys(tierErrors).length > 0) {
          setDiscountTierErrors(tierErrors);
          return;
        }
        setDiscountTierErrors({});
      }

      if (targetStep < step) {
        setIsPreviewStep(false);
        if (planId) {
          navigate(`/Plany/Edycja-planu/${planId}`, { replace: true });
        }
      }
      setStep(targetStep as 1 | 2 | 3 | 4);
    },
    [
      step,
      validateStep1,
      validateServicesStep,
      validateStep3EquipmentPrices,
      validateStep4DiscountTiers,
      planId,
      navigate,
    ]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!planId) {
        setSnackbarError(ERROR_MESSAGES.DEFAULT_ERROR);
        setIsSnackbarOpen(true);
        return;
      }

      // Step 1 "Next": validate and advance to Services — mirrors Create's
      // own step 1 → 2 transition. Plan-level fields are only ever validated
      // here, not re-checked again on later steps/preview submits.
      if (step === 1) {
        const newErrors = validateStep1();
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
          return;
        }
        setStep(2);
        return;
      }

      // Step 2 "Next": validate services, then advance to Equipment Pricing.
      if (step === 2) {
        const { fieldErrors, firstIncompleteKey, generalError } =
          validateServicesStep();
        if (firstIncompleteKey) {
          setServiceFieldErrors(fieldErrors);
          setExpandedServiceKey(firstIncompleteKey);
          setServicesError('');
          return;
        }
        if (generalError) {
          setServicesError(generalError);
          return;
        }
        setServicesError('');
        setStep(3);
        return;
      }

      // Step 3 "Next": validate equipment category prices, then advance to
      // Discount Logic.
      if (step === 3) {
        const priceErrors = validateStep3EquipmentPrices();
        if (Object.keys(priceErrors).length > 0) {
          setEquipmentPriceErrors(priceErrors);
          return;
        }
        setEquipmentPriceErrors({});
        setStep(4);
        return;
      }

      // Step 4: validate discount tiers before either entering preview (first
      // click) or submitting (second click, from preview).
      const tierErrors = validateStep4DiscountTiers();
      if (Object.keys(tierErrors).length > 0) {
        setDiscountTierErrors(tierErrors);
        return;
      }
      setDiscountTierErrors({});

      // First click: validation passed → go to "preview" step (button becomes "Zapisz")
      if (!isPreviewStep) {
        setIsPreviewStep(true);
        // Update URL to show "Podgląd planu" in breadcrumb
        if (planId) {
          navigate(`/Plany/Edycja-planu/${planId}/Podgląd-planu`, {
            replace: true,
          });
        }
        return;
      }

      // Second click: call API
      setIsSubmitting(true);
      setErrors({});

      try {
        const result = await editSubscriptionPlan({
          planId,
          planName: formData.planName.trim(),
          planDescription: formData.description.trim(),
          durationType,
          period,
          cycle,
          price: formData.price,
          ...(formData.userCount && { userCount: formData.userCount }),
          ...(formData.facilityCount && {
            facilityCount: formData.facilityCount,
          }),
          ...(formData.qmsDocCount && { qmsDocCount: formData.qmsDocCount }),
          ...(selectedOffer && { offerId: selectedOffer }),
          services: services.map((entry): EditSubscriptionPlanServiceInput => {
            const service: EditSubscriptionPlanServiceInput = {
              serviceTypeId: entry.serviceTypeId,
              usageLimit: entry.usageLimit,
              basePrice: entry.basePrice,
            };
            if (entry.discountPercentage) {
              service.discountPercentage = entry.discountPercentage;
            }
            if (entry.id) {
              service.id = entry.id;
            }
            return service;
          }),
          equipmentCategories: equipmentCategoryOptions.map((category) => ({
            equipmentCategoryId: category.id,
            basePrice: equipmentPrices[category.id] ?? '',
          })),
          discountTiers: Array.from(
            { length: EQUIPMENT_RANK_COUNT },
            (_, i) => ({
              equipmentRank: i + 1,
              discountPercentage: discountTiers[i + 1] ?? '0',
            })
          ),
        });

        if (result.success) {
          navigate('/Plany', {
            replace: true,
            state: { showEditPlanSuccess: true },
          });
          return;
        }

        if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
          // Nested paths like "services.2.basePrice" (dot-joined array
          // index — see lib/validation.ts's `err.path.join(".")`) don't
          // belong in the flat plan-level errors object; route them back to
          // the matching service card instead, same as a client-side
          // validation failure would.
          const planFieldErrors: Record<string, string> = {};
          const nextServiceFieldErrors: Record<
            string,
            ServiceConfigFieldErrors
          > = {};
          let firstErroringServiceKey: string | null = null;

          for (const [path, message] of Object.entries(result.fieldErrors)) {
            const serviceMatch = path.match(
              /^services\.(\d+)\.(serviceTypeId|usageLimit|basePrice)$/
            );
            if (serviceMatch) {
              const entry = services[Number(serviceMatch[1])];
              const field = serviceMatch[2] as keyof ServiceConfigFieldErrors;
              if (entry) {
                nextServiceFieldErrors[entry.key] = {
                  ...nextServiceFieldErrors[entry.key],
                  [field]: message,
                };
                firstErroringServiceKey ??= entry.key;
              }
              continue;
            }
            planFieldErrors[path] = message;
          }

          if (Object.keys(planFieldErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...planFieldErrors }));
          }
          if (Object.keys(nextServiceFieldErrors).length > 0) {
            setServiceFieldErrors((prev) => ({
              ...prev,
              ...nextServiceFieldErrors,
            }));
          }
          if (firstErroringServiceKey) {
            setExpandedServiceKey(firstErroringServiceKey);
          }
          // The preview screen is a read-only summary — it can't show an
          // inline field error, so drop back to the editable stepper.
          setIsPreviewStep(false);
        }
        const isPlanNameExistsError =
          result.error === ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS ||
          result.error
            ?.toLowerCase()
            .includes('subscription plan name already exists');
        if (isPlanNameExistsError) {
          setErrors((prev) => ({
            ...prev,
            planName: SUBSCRIPTION_ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS,
          }));
          setIsPreviewStep(false);
        } else if (result.error) {
          setSnackbarError(result.error);
          setIsSnackbarOpen(true);
        }
      } catch {
        setSnackbarError(ERROR_MESSAGES.NETWORK_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      durationType,
      period,
      cycle,
      navigate,
      planId,
      isPreviewStep,
      selectedOffer,
      services,
      step,
      validateStep1,
      validateServicesStep,
      validateStep3EquipmentPrices,
      validateStep4DiscountTiers,
      equipmentCategoryOptions,
      equipmentPrices,
      discountTiers,
    ]
  );

  // Cancel always means leave the whole flow (with confirmation if dirty),
  // regardless of preview state — "Back to Edit" is now its own separate
  // button for returning to the stepper without leaving.
  const handleCancel = useCallback(() => {
    if (!hasChanges) {
      navigate('/Plany', { replace: true });
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasChanges, navigate]);

  const handleBackToEdit = useCallback(() => {
    setIsPreviewStep(false);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    // Navigate back to edit path if we're in preview mode
    if (isPreviewStep && planId) {
      navigate(`/Plany/Edycja-planu/${planId}`, { replace: true });
    }
    navigate('/Plany', { replace: true });
  }, [navigate, isPreviewStep, planId]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  // Status change handler - opens dialog instead of directly changing status
  const handleStatusChange = useCallback(() => {
    setIsStatusDialogOpen(true);
  }, []);

  // Status dialog handlers
  const handleCloseStatusDialog = useCallback(() => {
    if (!isStatusSubmitting) {
      setIsStatusDialogOpen(false);
    }
  }, [isStatusSubmitting]);

  // Handle status update confirmation
  const handleConfirmStatusUpdate = useCallback(async () => {
    if (!planId) {
      setStatusErrorMessage(ERROR_MESSAGES.DEFAULT_ERROR);
      setShowStatusErrorSnackbar(true);
      return;
    }

    setIsStatusSubmitting(true);
    const isActive = status === 'inactive'; // Will activate if currently inactive

    try {
      const result =
        await updateSubscriptionStatusApiService.updateSubscriptionStatus({
          planId,
          isActive,
        });

      if (result.success) {
        // Update status state
        const newStatus: 'active' | 'inactive' = isActive
          ? 'active'
          : 'inactive';
        setStatus(newStatus);

        // Show success snackbar
        const message = isActive
          ? 'Plan subskrypcji został pomyślnie aktywowany.'
          : 'Plan subskrypcji został pomyślnie dezaktywowany.';
        setStatusSuccessMessage(message);
        setShowStatusSuccessSnackbar(true);
        setIsStatusDialogOpen(false);
      } else {
        // Show error snackbar
        setStatusErrorMessage(
          result.error || 'Wystąpił błąd podczas aktualizacji statusu.'
        );
        setShowStatusErrorSnackbar(true);
      }
    } catch (_error) {
      // Show error snackbar
      setStatusErrorMessage(
        'Wystąpił nieoczekiwany błąd podczas aktualizacji statusu.'
      );
      setShowStatusErrorSnackbar(true);
    } finally {
      setIsStatusSubmitting(false);
    }
  }, [planId, status]);

  // Status snackbar handlers
  const handleCloseStatusSuccessSnackbar = useCallback(() => {
    setShowStatusSuccessSnackbar(false);
    setStatusSuccessMessage('');
  }, []);

  const handleCloseStatusErrorSnackbar = useCallback(() => {
    setShowStatusErrorSnackbar(false);
    setStatusErrorMessage('');
  }, []);

  return {
    // Loading state
    isLoading,
    accessError,

    // Form state
    formData,
    setFormData,
    durationType,
    setDurationType,
    period,
    setPeriod,
    cycle,
    setCycle,
    cycleOptions,
    errors,
    setErrors,

    // Read-only fields
    modifiedBy,
    modifiedDate,

    // Wizard step state
    step,
    completedSteps,
    handleStepTabClick,

    // UI state
    isPreviewStep,
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    hasChanges,
    previewData,
    selectedOffer,
    setSelectedOffer,
    offerName,
    offerDiscount,
    setOfferDiscount,
    handleOfferChange,

    // Services state
    services,
    expandedServiceKey,
    serviceFieldErrors,
    servicesError,
    hasIncompleteService,
    removeDialogEntry,
    handleAddService,
    handleServiceTypeChange,
    handleServiceFieldChange,
    handleToggleExpandService,
    handleRequestRemoveService,
    handleCloseRemoveDialog,
    handleConfirmRemoveService,

    // Equipment Pricing state
    equipmentCategoryOptions,
    equipmentCategoriesLoading,
    equipmentPrices,
    equipmentPriceErrors,
    handleEquipmentPriceChange,

    // Discount Logic state
    discountTiers,
    discountTierErrors,
    handleDiscountTierChange,

    // Handlers
    handleSubmit,
    handleCancel,
    handleBackToEdit,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,

    // Helpers (for form JSX)
    formatPriceWithSpaces,
    getBillingDescription,

    // Status management
    status,
    activeClientCount,
    handleStatusChange,
    isStatusDialogOpen,
    handleCloseStatusDialog,
    handleConfirmStatusUpdate,
    isStatusSubmitting,
    showStatusSuccessSnackbar,
    statusSuccessMessage,
    handleCloseStatusSuccessSnackbar,
    showStatusErrorSnackbar,
    statusErrorMessage,
    handleCloseStatusErrorSnackbar,
  };
}
